import { PropsWithChildren, useState } from "react";
import { Row, Container } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnglesLeft, faAnglesRight } from "@fortawesome/free-solid-svg-icons";

// function buildParams(page: number, search: string | null = null) {
//     const searchArg = search ? `&search=${search}` : '';
//     return `page=${page}${searchArg}`
// }

interface PageNavProps {
    page?: number | null
    maxPage: number | null
    onChange: ((page: number) => void)
    totalCount?: number
}

function prepareParams(searchParams: URLSearchParams, ...params: string[]) {
    const result: { [key: string]: string } = {};

    for (const param of params) {
        const value = searchParams.get(param);
        if (value !== null) {
            result[param] = value;
        }
    }
    return result;
}

export default function PageNav(props: PropsWithChildren<PageNavProps>) {
    const currentPage = props.page || 1;
    const [page, setPage] = useState<number>(Math.max(currentPage, 1));

    const [searchParams, setSearchParams] = useSearchParams();

    const goToPage = (newPage: number) => {
        setPage(newPage);
        setSearchParams({
            ...prepareParams(searchParams, "search"),
            page: newPage.toString(),
        });
        window.scrollTo({ top: 0, behavior: 'instant' });
        props.onChange(newPage);
    }

    const lastPage = props.maxPage || 1;

    // Generate page numbers to show (up to 5 pages around current)
    const getPageNumbers = () => {
        const pages: number[] = [];
        const start = Math.max(1, page - 2);
        const end = Math.min(lastPage, page + 2);

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
        <Container fluid={true} style={{ maxWidth: "28rem" }}>
            <Row className="mx-auto justify-content-center">
                {page > 1 && <>
                    <button data-testid="pagination-first" aria-label="First" className="mx-1 col-auto btn btn-sm btn-outline-dark" onClick={() => goToPage(1)} disabled={page === 1}><FontAwesomeIcon className="mt-1" icon={faAnglesLeft} /></button>
                    <button data-testid="pagination-prev" aria-label="Previous" className="mx-1 col-auto btn btn-sm btn-outline-dark" onClick={() => goToPage(page - 1)}>Previous</button>
                </>}
                {pageNumbers[0] > 2 && <span className="col-auto align-self-center px-1">...</span>}
                {pageNumbers.map(p => {
                    if (p === page) {
                        return (
                            <button
                                key={p}
                                data-testid="pagination-current"
                                aria-label="Current"
                                className="mx-1 col-auto btn btn-sm btn-dark"
                                disabled
                            >
                                {p}
                            </button>
                        );
                    }
                    if (p !== 1 && p !== lastPage) {
                        return (
                            <button
                                key={p}
                                data-testid={`pagination-${p}`}
                                aria-label={`Page ${p}`}
                                className="mx-1 col-auto btn btn-sm btn-outline-dark"
                                onClick={() => goToPage(p)}
                            >
                                {p}
                            </button>
                        );
                    }
                    return null;
                })}
                {pageNumbers[pageNumbers.length - 1] < lastPage - 1 && <span className="col-auto align-self-center px-1">...</span>}
                {page < lastPage && <button data-testid="pagination-next" aria-label="Next" className="mx-1 col-auto btn btn-sm btn-outline-dark" onClick={() => goToPage(page + 1)}>Next</button>}
                {lastPage > 1 && <button data-testid="pagination-last" aria-label="Last" className="mx-1 col-auto btn btn-sm btn-outline-dark" onClick={() => goToPage(lastPage)} disabled={page === lastPage}><FontAwesomeIcon className="mt-1" icon={faAnglesRight} /></button>}
            </Row>
            {props.totalCount ?
                <div className="row text-center">
                    <span className="pt-2 fw-lighter">
                        <i>Total notifications: {props.totalCount}</i>
                    </span>
                </div>
                : null}
        </Container>
    );
}